import React, { useEffect, useRef, useState, useContext } from "react";
import styles from "./Root.module.css";
import Modal from "common-ui-components/components/modal/Modal";
import {
  clearAuthToken,
  setCustomerIdInCookies,
} from "common-ui-components/accounts/framework/AuthTokenHandler";
import { IAM_API } from "../../common-ui-components/components/utils/IAM_API";
import ErrorBoundary from "./ErrorBoundary";
import { UserDetailsContext } from "../../App";
import Subscription from "../settings/organization/subscription/Subscription";
import Trailended, {
  TechPlanEnded,
  BlockedMessagePage,
} from "../settings/organization/subscription/manage-subscription/Trailended";
import Manage from "../settings/organization/subscription/manage-subscription/Manage";
import { useLocation, useNavigate } from "react-router-dom";
import {
  hideLoader,
  showLoader,
} from "common-ui-components/components/ResponseLoader/ResponseLoader";
import {
  IAM_FRONT_END_URL,
  PRODUCT_CONTEXT,
  IS_MSP,
} from "common-ui-components/components/utils/Config";
import { useRestrictionChecker } from "common-ui-components/components/restrictionUI/RestrictionChecker";
import ProductSwitch from "./product switch/ProductSwitch";
import { Products } from "ProductConfig";
import Button from "common-ui-components/components/button";
import CustomerSwitch from "./customer-switch/CustomerSwitch";
import RootSidebar from "./RootSidebar";
import RootNavbarLogoFavicon from "./RootNavbarLogoFavicon";
import ProfileImage from "./ProfileImage/ProfileImage";
import { isMspProduct, redirectToContactPage } from "utils/mspBasicUtils";
import Support, { SupportTrigger } from "./support/Support";
import UpgradeModal from "./upgrade/Upgrade";
import upgradeStyles from "./upgrade/UpgradeModal.module.css";
import ReviewPromptModal from "./review/ReviewPromptModal";

// ---------------- MSP Related utils and global variables------------------------
//storing the customerid for some api calling
let currentCustomerId = 1;
export const setCurrentCustomerIdGlobal = (id) => {
  currentCustomerId = id;
};
export const getCurrentCustomerId = () => currentCustomerId; // util to know the current custoner id

// customer switching is recorded and stored globally for accessing
let isCustomerSwitching = false;
export const setCustomerSwitching = (value) => {
  isCustomerSwitching = value;
};
export const getCustomerSwitchingStatus = () => isCustomerSwitching; //util to know whether customer is chnaging
// ----------------------------------xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx---------------------------------------

//root page dimensions
export const VERTICAL_BAR_WIDTH = "70px";
export const HORIZONTAL_BAR_HEIGHT = "56px";

const buttonStyles = { background: "#DA2628", border: "none" };
const UPGRADE_PROMPT_INTERVAL = 2 * 60 * 60 * 1000;
const REVIEW_PROMPT_REMIND_LATER_DELAY = 2 * 24 * 60 * 60 * 1000;
const REVIEW_PROMPT_DECLINE_DELAY = 7 * 24 * 60 * 60 * 1000;

const RenewButton = ({ onClick }) => {
  return (
    <Button variant="primary" onClick={onClick} style={buttonStyles}>
      Renew
    </Button>
  );
};

const BuyNowButton = ({ onClick }) => {
  return (
    <Button variant="primary" onClick={onClick} style={buttonStyles}>
      Buy Now
    </Button>
  );
};

const Root = ({ children, hideSideBar = false }) => {
  const userDetails = useContext(UserDetailsContext);

  const [customerDropdownMode, setCustomerDropdownMode] = useState(null);

  //related to subscription and payment
  const [isPayment, setIsPayment] = useState(false);
  const [showStatus, setShowStatus] = useState(null);
  const [isAccountBlocked, setIsAccountBlocked] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [reviewPromptOpen, setReviewPromptOpen] = useState(true);
  const [renew, setRenew] = useState(false);
  const wordingRef = useRef("Plan");
  const daysLeft = userDetails.daysLeft;
  const subscriptionStatus = userDetails.subscriptionStatus;
  const orgStatus = userDetails.orgStatus;
  const plan = userDetails.plan;

  //handles restriction ui
  const { checkRestriction } = useRestrictionChecker();
  const canView = !checkRestriction("SUBSCRIPTIONS_VIEW", true);
  const navigate = useNavigate();

  const location = useLocation();
  const isTryProductPath = location.pathname.includes("/try-product");
  const isPaidPlan =
    Boolean(plan) && !["free", "trial"].includes(plan.toLowerCase());

  const UPGRADE_PROMPT_KEY = `upgradePromptLastShownAt_${PRODUCT_CONTEXT}`;
  const CUSTOMER_REVIEW_PROMPT_KEY = `reviewPromptNextAt_${PRODUCT_CONTEXT}_${userDetails.currentCustomerId}`;
  const REVIEW_PROMPT_KEY = IS_MSP
    ? `reviewPromptNextAt_${PRODUCT_CONTEXT}`
    : CUSTOMER_REVIEW_PROMPT_KEY;

  // Consolidated logic into a single useEffect
  useEffect(() => {
    setIsAccountBlocked(false);
    setShowStatus(null);
    setIsFreePlan(false);

    if (userDetails.expired === true || daysLeft === -1) {
      setIsAccountBlocked(true);
    } else if (plan === "Trial") {
      wordingRef.current = "Trial";
      setShowStatus("trial");
    } else if (plan === "Free") {
      wordingRef.current = "Free";
      setIsFreePlan(true);
    } else if (subscriptionStatus === "GRACE_PERIOD") {
      setShowStatus("grace");
      wordingRef.current = "Grace Period";
    } else if (daysLeft <= 15) {
      setShowStatus("nearingExpiration");
      wordingRef.current = "Plan";
    }
  }, [plan, daysLeft, userDetails.expired, subscriptionStatus]);

  useEffect(() => {
    if (plan !== "Free" || !canView || isTryProductPath) return undefined;

    const showUpgradePrompt = () => {
      sessionStorage.setItem(UPGRADE_PROMPT_KEY, String(Date.now()));
      setUpgradeOpen(true);
    };

    const lastShownAt = Number(sessionStorage.getItem(UPGRADE_PROMPT_KEY));
    const elapsed = Number.isFinite(lastShownAt)
      ? Date.now() - lastShownAt
      : UPGRADE_PROMPT_INTERVAL;
    const timeUntilNextPrompt = Math.max(UPGRADE_PROMPT_INTERVAL - elapsed, 0);

    if (timeUntilNextPrompt === 0) {
      showUpgradePrompt();
      return undefined;
    }

    const timer = window.setTimeout(showUpgradePrompt, timeUntilNextPrompt);
    return () => window.clearTimeout(timer);
  }, [plan, canView, isTryProductPath, UPGRADE_PROMPT_KEY]);

  useEffect(() => {
    if (!isPaidPlan || isAccountBlocked || isTryProductPath) {
      setReviewPromptOpen(false);
      return;
    }

    if (IS_MSP && !localStorage.getItem(REVIEW_PROMPT_KEY)) {
      const customerNextPromptAt = localStorage.getItem(
        CUSTOMER_REVIEW_PROMPT_KEY,
      );
      if (customerNextPromptAt) {
        localStorage.setItem(REVIEW_PROMPT_KEY, customerNextPromptAt);
      }
    }

    const nextPromptAt = Number(localStorage.getItem(REVIEW_PROMPT_KEY));
    setReviewPromptOpen(
      !Number.isFinite(nextPromptAt) || nextPromptAt <= Date.now(),
    );
    // Only for testing the thing ->> always show
    // setReviewPromptOpen(true);
  }, [
    isPaidPlan,
    isAccountBlocked,
    isTryProductPath,
    REVIEW_PROMPT_KEY,
    CUSTOMER_REVIEW_PROMPT_KEY,
  ]);

  //a useeffect for chnaginf the customer ids when vere there is a change in currenct customer id
  useEffect(() => {
    const id = userDetails.currentCustomerId;
    // Skip until Main.js has actually resolved and set a real customer id
    // (avoids running with a stale/undefined value during initial pending state)
    if (id === undefined || id === null) return;
    //local storage setting used because to get last chhosed customer (browser)
    localStorage.setItem("customerId", id);
    //cookie setting is for backend purposes
    setCustomerIdInCookies(id);
    //global variable setting
    setCurrentCustomerIdGlobal(id);
    // allow api call requests only after customer id updated... pre calling is restricted
    setCustomerSwitching(false);
  }, [userDetails.currentCustomerId]);

  const handleRenewClick = () => {
    if (isMspProduct()) {
      redirectToContactPage();
    } else {
      navigate("/app/settings/account/subscription");
    }
  };

  const handleBuyNowClick = () => {
    navigate("/app/settings/account/subscription");
  };

  const deferReviewPrompt = (delay) => {
    localStorage.setItem(REVIEW_PROMPT_KEY, String(Date.now() + delay));
    setReviewPromptOpen(false);
  };

  return (
    <ErrorBoundary>
      <div
        style={{
          "--vertical-bar-width": hideSideBar ? 0 : VERTICAL_BAR_WIDTH,
          "--horizontal-bar-height": HORIZONTAL_BAR_HEIGHT,
          position: "fixed",
          width: "100%",
        }}
        id="root-container"
        className={styles.rootContainer}
      >
        <div className={styles.rootHorizontalBar}>
          <div className={styles.companyName}>
            <RootNavbarLogoFavicon />
            <div className={styles.productName}>
              {Products[PRODUCT_CONTEXT]}
            </div>
          </div>

          <div className={styles.rightSection}>
            {showStatus && !isTryProductPath && (
              <section className={styles.trail}>
                {daysLeft === 0 && (
                  <p className={styles.time}>
                    {wordingRef.current} expires Today.
                  </p>
                )}
                {daysLeft > 0 && (
                  <p className={styles.time}>
                    {wordingRef.current} expires in
                    <b> {daysLeft}</b> days.
                  </p>
                )}
                {showStatus === "nearingExpiration"
                  ? orgStatus !== "PAYMENT_SUSPENDED" && (
                      <RenewButton onClick={handleRenewClick} />
                    )
                  : orgStatus !== "PAYMENT_SUSPENDED" && (
                      <BuyNowButton onClick={handleBuyNowClick} />
                    )}
              </section>
            )}
            {isFreePlan && canView && !isTryProductPath && (
              <section className={styles.trail}>
                <p className={styles.time}>
                  You are on the {wordingRef.current} plan.
                </p>
                {orgStatus !== "PAYMENT_SUSPENDED" && (
                  <BuyNowButton onClick={handleBuyNowClick} />
                )}
              </section>
            )}

            <div className={styles.controls}>
              <SupportTrigger />
              {!IS_MSP && orgStatus !== "PAYMENT_SUSPENDED" && (
                <ProductSwitch />
              )}
              {!isAccountBlocked && IS_MSP && (
                <CustomerSwitch
                  setMode={setCustomerDropdownMode}
                  mode={customerDropdownMode}
                />
              )}
              <ProfileImage />
            </div>
          </div>

          <Modal open={isPayment} setOpen={setIsPayment}>
            <div style={{ height: "100vh", width: "70vw" }}>
              {isPayment && (
                <Subscription settings={true} setIsPayment={setIsPayment} />
              )}
            </div>
          </Modal>

          <Modal open={renew} setOpen={setRenew}>
            <div style={{ height: "75vh", width: "70vw", padding: "1rem" }}>
              {renew && <Manage modal setOpen={setRenew} />}
            </div>
          </Modal>

          <Modal
            open={upgradeOpen}
            setOpen={setUpgradeOpen}
            className={upgradeStyles.dialog}
          >
            <UpgradeModal
              planName="Free"
              upgradeName={
                PRODUCT_CONTEXT === "em" || PRODUCT_CONTEXT === "em-msp"
                  ? "Professional"
                  : "Standard"
              }
              product={PRODUCT_CONTEXT == "em-msp" ? "em" : PRODUCT_CONTEXT}
              onClose={() => setUpgradeOpen(false)}
            />
          </Modal>

          <ReviewPromptModal
            isOpen={reviewPromptOpen}
            setOpen={setReviewPromptOpen}
            onClose={() => deferReviewPrompt(REVIEW_PROMPT_REMIND_LATER_DELAY)}
            onRemindLater={() =>
              deferReviewPrompt(REVIEW_PROMPT_REMIND_LATER_DELAY)
            }
            onDecline={() => deferReviewPrompt(REVIEW_PROMPT_DECLINE_DELAY)}
            onWriteReview={() => deferReviewPrompt(REVIEW_PROMPT_DECLINE_DELAY)}
            onOffersClick={() => {
              deferReviewPrompt(REVIEW_PROMPT_REMIND_LATER_DELAY);
              navigate("/app/settings/account/offer");
            }}
          />
        </div>

        {orgStatus === "PAYMENT_SUSPENDED" ? (
          <>
            <BlockedMessagePage />
          </>
        ) : isAccountBlocked ? (
          <div>
            {userDetails.roleName === "Super Admin" ? (
              <Trailended />
            ) : (
              <TechPlanEnded />
            )}
          </div>
        ) : (
          <>
            {!hideSideBar && <RootSidebar verified={userDetails?.verified} />}

            <div
              className={styles.rootChildren}
              data-page-restrict-content
              style={
                userDetails &&
                userDetails.hasOwnProperty("verified") &&
                !userDetails.verified
                  ? { paddingBottom: "35px" }
                  : null
              }
            >
              {children}
            </div>
          </>
        )}
      </div>
    </ErrorBoundary>
  );
};

const logOut = async () => {
  showLoader(true, true);
  try {
    await IAM_API.get("/user/logout");
  } catch (err) {
    console.error("Logout failed", err);
  }
  redirectToSignIn();
  hideLoader();
};

const redirectToSignIn = async () => {
  window.localStorage.clear();
  const accessedUrl = window.location.href;
  const iamServer = `${IAM_FRONT_END_URL}/auth`;
  let signInUrl = `${iamServer}/sign-in`;
  if (PRODUCT_CONTEXT !== "") {
    const route = `/${PRODUCT_CONTEXT}`;
    let redirectPath = route;
    if (accessedUrl && accessedUrl.includes(route)) {
      const startIndex = accessedUrl.indexOf(route);
      redirectPath = accessedUrl.substring(startIndex);
    }
    const redirectUrl = redirectPath;
    signInUrl = `${iamServer}/sign-in?redirectUrl=${redirectUrl}`;
  }
  clearAuthToken();
  setTimeout(() => {
    window.location.href = signInUrl;
  }, 500);
};

export { Root, logOut, redirectToSignIn };

// This function helps in identifying which submodule is rendered based on the current path and the provided route list. It checks if the current path starts with any of the paths defined in the route list and returns the corresponding key of that route. If no match is found, it returns "UNKNOWN".
export const getCurrentlyRenderedSubModule = (routeList = null) => {
  const rawPath = window.location.pathname;
  const resultPath = `/${rawPath.split("/").slice(2).join("/")}`;
  if (!routeList) return "No route list is passed";
  for (const route of routeList) {
    let routePath = route.path || route.url;
    if (routePath) {
      // Remove trailing wildcard /* for accurate matching
      routePath = routePath.replace(/\/?\*$/, "");
      if (resultPath.startsWith(routePath)) {
        return route.key;
      }
    }
  }
  return "UNKNOWN";
};
